import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Currency } from '../data/productModels';

interface CurrencyContextType {
  currency: Currency;
  setCurrency: (c: Currency) => void;
  formatPrice: (cents: number | null | undefined) => string;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

const STORAGE_KEY = 'natuvia_currency';
const SUPPORTED_CURRENCIES: Currency[] = ['HKD', 'USD', 'GBP', 'CNY'];

export const CurrencyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currency, setCurrencyState] = useState<Currency>('HKD');

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && SUPPORTED_CURRENCIES.includes(saved as Currency)) {
        setCurrencyState(saved as Currency);
      }
    } catch (err) {
      console.warn('Failed to load currency', err);
    }
  }, []);

  const setCurrency = (c: Currency) => {
    setCurrencyState(c);
    try {
      localStorage.setItem(STORAGE_KEY, c);
    } catch {
      // Ignore storage errors (e.g. private browsing on iOS Safari)
    }
    window.dispatchEvent(new CustomEvent('currency-change', { detail: c }));
  };

  const formatPrice = (cents: number | null | undefined): string => {
    if (cents === null || cents === undefined) return '';

    // Safety check for NaN
    if (isNaN(cents)) return '';

    const val = cents / 100;

    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: currency === 'HKD' ? 0 : 2, // HKD usually no cents, USD/GBP yes
        maximumFractionDigits: 2,
      }).format(val);
    } catch {
      return `${currency} ${val.toFixed(2)}`;
    }
  };

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, formatPrice }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) throw new Error('useCurrency must be used within a CurrencyProvider');
  return context;
};
